import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { generateTimesheetPdf } from "@/lib/timesheetPdf";

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export const TimesheetDialog = ({ employee }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const now = new Date();
  const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);
  const [loading, setLoading] = useState(false);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const { data } = await api.getEmployeeTimesheet(employee.id, { year, month });
      if (!data.submissions || data.submissions.length === 0) {
        toast.error("Aucune soumission approuvée sur ce mois");
        return;
      }
      generateTimesheetPdf(data, { companyName: user?.name || "Bati Assistant" });
      toast.success(`Fiche de temps PDF générée (${data.summary?.total_hours?.toFixed(1) || 0}h)`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur génération fiche de temps");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="open-timesheet-dialog"
        title="Générer la fiche de temps mensuelle signée"
      >
        <ClipboardCheck className="h-4 w-4 mr-2" />
        Fiche de temps
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="timesheet-dialog">
          <DialogHeader>
            <DialogTitle>Fiche de temps mensuelle signée</DialogTitle>
            <DialogDescription>
              Regroupe toutes les soumissions d'heures <strong>approuvées</strong> du mois
              avec leur signature numérique pour archivage légal.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="ts-month">Mois</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger id="ts-month" data-testid="timesheet-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS_FR.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ts-year">Année</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger id="ts-year" data-testid="timesheet-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>Annuler</Button>
            <Button onClick={handleGenerate} disabled={loading} data-testid="timesheet-generate-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
              Générer le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TimesheetDialog;
